/* Public e-sign page — NO authentication required (the token in the URL is the auth).
   Branded landing that previews the proposal, captures a drawn or typed signature, and
   confirms with a cryptographic content hash audit receipt.
*/
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import SignatureCanvas from "react-signature-canvas";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, ShieldCheck, FileSignature, Lock, AlertCircle, Eraser, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtGhs = (n) => (n == null ? "—" : `GH₵ ${Number(n).toLocaleString()}`);

export default function Sign() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | error | submitting | success
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("draw"); // draw | type
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [typedName, setTypedName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const padRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/sign/${token}`);
        setData(r.data);
        setSignerName(r.data.signer_name || "");
        setSignerEmail(r.data.signer_email || "");
        setState("ready");
      } catch (e) {
        setError(e?.response?.data?.detail || "This link is invalid or has expired.");
        setState("error");
      }
    })();
  }, [token]);

  const clear = () => padRef.current && padRef.current.clear();

  const submit = async () => {
    if (!signerName || !signerEmail) return setError("Name and email are required.");
    if (!agreed) return setError("You must accept the signing terms.");
    let signature_data_url = "";
    let typed = "";
    if (mode === "draw") {
      if (!padRef.current || padRef.current.isEmpty()) return setError("Please draw your signature.");
      signature_data_url = padRef.current.getTrimmedCanvas().toDataURL("image/png");
    } else {
      if (!typedName.trim()) return setError("Type your full legal name as your signature.");
      typed = typedName.trim();
    }
    setState("submitting");
    setError("");
    try {
      const r = await axios.post(`${API}/sign/${token}/sign`, {
        signer_name: signerName,
        signer_email: signerEmail,
        signer_title: signerTitle,
        signature_data_url,
        typed_name: typed,
      });
      setReceipt(r.data);
      setState("success");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit signature");
      setState("ready");
    }
  };

  if (state === "loading") {
    return <FullPage><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></FullPage>;
  }
  if (state === "error") {
    return (
      <FullPage>
        <div className="nexus-glass-panel p-10 max-w-md text-center">
          <img src="/pandora-nexus-mark.png" alt="Pandora Nexus" className="h-10 mx-auto mb-6 object-contain opacity-90" />
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto" strokeWidth={1.2} />
          <h1 className="font-display text-2xl text-white mt-4">Cannot open signing link</h1>
          <p className="text-slate-400 mt-2 text-sm">{error}</p>
          <p className="text-slate-500 text-[11px] font-mono tracking-widest mt-6">PANDORA MANAGEMENT CONSULT LTD</p>
        </div>
      </FullPage>
    );
  }
  if (state === "success") {
    return (
      <FullPage>
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="nexus-glass-panel p-10 max-w-lg w-full"
          data-testid="sign-success"
        >
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, rgba(34,197,94,0.18), rgba(34,197,94,0.05))", border: "1px solid rgba(34,197,94,0.3)" }}>
            <CheckCircle2 className="w-7 h-7 text-emerald-400" strokeWidth={1.5} />
          </div>
          <h1 className="font-display text-3xl text-white text-center mt-5">Signature captured</h1>
          <p className="text-slate-400 text-center mt-2 text-sm">
            Your signature has been recorded against the proposal and a copy has been securely filed.
          </p>
          <div className="mt-7 grid grid-cols-1 gap-2 text-[12px]">
            <Row k="Signed by" v={receipt.signer_name} />
            <Row k="Signed at" v={new Date(receipt.signed_at).toLocaleString()} />
            <Row k="Issued by" v={receipt.issuer} />
            <Row k="Content hash" v={<span className="font-mono text-[10px] break-all">{receipt.content_hash}</span>} />
          </div>
          <div className="mt-6 text-[10px] text-slate-500 font-mono tracking-widest text-center">
            CRYPTOGRAPHIC AUDIT HASH (SHA-256) · NON-REPUDIATION ENABLED
          </div>
        </motion.div>
      </FullPage>
    );
  }

  const p = data.proposal;
  return (
    <FullPage>
      <div className="max-w-5xl w-full grid grid-cols-1 lg:grid-cols-5 gap-6 mx-auto">
        {/* Document preview */}
        <div className="lg:col-span-3 nexus-glass-panel p-8 max-h-[88vh] overflow-y-auto">
          <div className="flex items-center gap-2 label-eyebrow"><FileSignature className="w-3 h-3" /> {p.type?.replace(/_/g, " ")}</div>
          <h1 className="font-display text-2xl text-white mt-2">{p.title}</h1>
          <div className="mt-2 flex items-center gap-3 text-[11px] font-mono">
            <span className="text-slate-400">Client: {p.client_name}</span>
            <span className="text-slate-700">|</span>
            <span className="text-[#D4AF37]">{fmtGhs(p.value)}</span>
            <span className="text-slate-700">|</span>
            <span className="text-slate-400">v{p.version}</span>
          </div>
          <div className="mt-6 nexus-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.content}</ReactMarkdown>
          </div>
        </div>

        {/* Sign panel */}
        <div className="lg:col-span-2 space-y-4">
          <div className="nexus-glass-panel p-6">
            <div className="flex items-center gap-2 label-eyebrow mb-1"><Lock className="w-3 h-3" /> Secure signing</div>
            <h2 className="font-display text-xl text-white">Authorize agreement</h2>
            <p className="text-[12px] text-slate-400 mt-1">Issued by <span className="text-white">{data.issuing_org}</span></p>
            <div className="mt-5 space-y-3">
              <Field label="Full legal name *">
                <input data-testid="signer-name" value={signerName} onChange={(e) => setSignerName(e.target.value)} className="nexus-input" />
              </Field>
              <Field label="Email *">
                <input data-testid="signer-email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} className="nexus-input" />
              </Field>
              <Field label="Title">
                <input data-testid="signer-title" value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} className="nexus-input" placeholder="e.g., Chief Executive Officer" />
              </Field>
            </div>
          </div>

          <div className="nexus-glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="label-eyebrow">Signature</div>
              <div className="inline-flex border border-white/10 rounded-full overflow-hidden">
                {[
                  { id: "draw", label: "DRAW" },
                  { id: "type", label: "TYPE" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setMode(opt.id)}
                    data-testid={`sig-mode-${opt.id}`}
                    className={`px-3 py-1 font-mono text-[10px] tracking-widest ${mode === opt.id ? "bg-[#D4AF37] text-[#050B14]" : "text-slate-400 hover:text-white"}`}
                  >{opt.label}</button>
                ))}
              </div>
            </div>

            {mode === "draw" ? (
              <>
                <div className="relative bg-white rounded-lg overflow-hidden" data-testid="signature-pad">
                  <SignatureCanvas
                    ref={padRef}
                    penColor="#0A0B14"
                    canvasProps={{ width: 360, height: 160, className: "w-full" }}
                  />
                  <button onClick={clear} className="absolute top-2 right-2 p-1.5 rounded-md bg-slate-200 hover:bg-slate-300 text-slate-700">
                    <Eraser className="w-3 h-3" />
                  </button>
                </div>
                <div className="text-[10px] text-slate-500 font-mono tracking-widest mt-2 text-center">DRAW WITH MOUSE OR FINGER</div>
              </>
            ) : (
              <div>
                <input
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  data-testid="typed-signature"
                  className="w-full text-center px-4 py-6 bg-white text-[#0A0B14] rounded-lg outline-none font-display text-2xl italic"
                  placeholder="Type your full name"
                  style={{ fontFamily: "'Cabinet Grotesk', cursive" }}
                />
                <div className="text-[10px] text-slate-500 font-mono tracking-widest mt-2 text-center">
                  TYPED SIGNATURE — LEGALLY BINDING UNDER GHANAIAN ELECTRONIC TRANSACTIONS ACT 772
                </div>
              </div>
            )}

            <label className="flex items-start gap-2 mt-5 text-[12px] text-slate-300 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="agree-checkbox" className="mt-0.5" />
              <span>
                I confirm that I am the named individual, I have read this {p.type?.replace(/_/g, " ")}, and I agree to be
                legally bound by its terms. I acknowledge that my electronic signature is enforceable under the
                <span className="text-white"> Ghanaian Electronic Transactions Act, 2008 (Act 772)</span>.
              </span>
            </label>

            {error && (
              <div className="mt-3 text-[12px] text-red-400 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5" /> {error}
              </div>
            )}

            <button
              onClick={submit}
              disabled={state === "submitting"}
              data-testid="sign-submit"
              className="mt-5 w-full py-3 rounded-xl font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
              style={{ background: "linear-gradient(135deg, #FCE588 0%, #D4AF37 100%)", color: "#050B14" }}
            >
              {state === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Sign & finalize
            </button>

            <div className="mt-3 text-[10px] text-slate-600 font-mono tracking-widest text-center">
              IP & TIMESTAMP RECORDED · SHA-256 CONTENT BINDING
            </div>
          </div>

          <Link to="/login" className="flex flex-col items-center gap-2 hover:opacity-100 transition-opacity">
            <img src="/pandora-nexus-mark.png" alt="Pandora Nexus" className="h-10 object-contain opacity-90"
                 style={{ filter: "drop-shadow(0 0 12px rgba(212,175,55,0.25))" }} />
            <span className="text-[10px] text-slate-600 font-mono tracking-widest hover:text-[#D4AF37]">
              POWERED BY PANDORA NEXUS
            </span>
          </Link>
        </div>
      </div>
    </FullPage>
  );
}

const FullPage = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden" style={{ background: "var(--bg-app)" }}>
    <div className="fixed inset-0 pointer-events-none">
      <div className="absolute top-[-20%] left-[15%] w-[600px] h-[600px] rounded-full bg-[#D4AF37] opacity-[0.05] blur-[120px]" />
      <div className="absolute bottom-[-25%] right-[10%] w-[700px] h-[700px] rounded-full bg-[#00F0FF] opacity-[0.04] blur-[120px]" />
    </div>
    <div className="relative z-10 w-full flex justify-center">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-white/5">
    <span className="label-eyebrow shrink-0">{k}</span>
    <span className="text-white text-right break-all">{v}</span>
  </div>
);
